import { useState, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, Phone, Mail, ArrowRight } from "lucide-react";
import { COMPANY, NAV_LINKS } from "@/data/site";

const Logo = () => (
  <Link to="/" className="flex items-center gap-3 group" data-testid="header-logo">
    <img src="/assets/logo.png" alt="Daksh DDS Security Service Pvt. Ltd. logo" className="h-12 w-auto rounded-sm shadow-sm" />
    <div className="leading-tight hidden sm:block">
      <span className="block font-heading font-extrabold text-navy text-lg tracking-tight">DAKSH</span>
      <span className="block text-[10px] font-medium text-slate-500 tracking-[0.18em] uppercase">Detective & Security Services</span>
    </div>
  </Link>
);

const Header = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => setOpen(false), [location.pathname]);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <>
      <div className="bg-navy text-slate-300 text-xs border-b border-white/10" data-testid="top-contact-bar">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-2 flex flex-wrap items-center justify-between gap-2">
          <p className="hidden md:block tracking-wide">Trusted workforce partner since 2003 — Chandigarh, India</p>
          <div className="flex items-center gap-5">
            <a href={COMPANY.phoneHref} className="flex items-center gap-1.5 hover:text-gold transition-colors" data-testid="topbar-phone-link">
              <Phone size={12} className="text-gold" /> Call {COMPANY.phone}
            </a>
            <a href={COMPANY.emailHref} className="hidden sm:flex items-center gap-1.5 hover:text-gold transition-colors" data-testid="topbar-email-link">
              <Mail size={12} className="text-gold" /> {COMPANY.email}
            </a>
          </div>
        </div>
      </div>

      <header className={`sticky top-0 z-50 bg-white/95 backdrop-blur-md border-b border-slate-200/80 transition-shadow ${scrolled ? "shadow-md" : "shadow-sm"}`} data-testid="main-header">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-[72px]">
            <Logo />
            <nav className="hidden lg:flex items-center gap-7" aria-label="Main navigation">
              {NAV_LINKS.map((link) => (
                <NavLink
                  key={link.path}
                  to={link.path}
                  data-testid={`nav-${link.label.toLowerCase().replace(/\s+/g, "-")}-link`}
                  className={({ isActive }) =>
                    `text-sm font-medium tracking-wide transition-colors hover:text-gold-hover relative ${
                      isActive ? "text-navy after:absolute after:-bottom-2 after:left-0 after:right-0 after:h-0.5 after:bg-gold" : "text-slate-600"
                    }`
                  }
                >
                  {link.label}
                </NavLink>
              ))}
            </nav>
            <div className="flex items-center gap-3">
              <Link
                to="/request-quote"
                data-testid="header-quote-btn"
                className="hidden sm:inline-flex items-center gap-2 bg-gold hover:bg-gold-hover text-navy font-heading font-bold text-xs tracking-widest uppercase px-5 py-3 transition-all hover:gap-3"
              >
                Request a Quote <ArrowRight size={14} />
              </Link>
              <button
                className="lg:hidden p-2 text-navy"
                onClick={() => setOpen(!open)}
                aria-label="Toggle menu"
                data-testid="mobile-menu-toggle"
              >
                {open ? <X size={24} /> : <Menu size={24} />}
              </button>
            </div>
          </div>
        </div>

        <AnimatePresence>
          {open && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: "auto", opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
              className="lg:hidden overflow-hidden bg-white border-t border-slate-100"
              data-testid="mobile-menu"
            >
              <nav className="px-4 py-4 flex flex-col" aria-label="Mobile navigation">
                {NAV_LINKS.map((link, i) => (
                  <motion.div key={link.path} initial={{ opacity: 0, x: -12 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: i * 0.04 }}>
                    <NavLink
                      to={link.path}
                      data-testid={`mobile-nav-${link.label.toLowerCase().replace(/\s+/g, "-")}-link`}
                      className={({ isActive }) =>
                        `block py-3 px-2 text-sm font-medium border-b border-slate-100 ${isActive ? "text-gold-hover" : "text-slate-700"}`
                      }
                    >
                      {link.label}
                    </NavLink>
                  </motion.div>
                ))}
                <Link
                  to="/request-quote"
                  data-testid="mobile-quote-btn"
                  className="mt-4 inline-flex justify-center items-center gap-2 bg-gold text-navy font-heading font-bold text-xs tracking-widest uppercase px-5 py-3.5"
                >
                  Request a Quote <ArrowRight size={14} />
                </Link>
              </nav>
            </motion.div>
          )}
        </AnimatePresence>
      </header>
    </>
  );
};

export default Header;
